/**
 * Utility functions for formatting extracted PDF text to improve readability
 */

export interface TextFormattingOptions {
  preserveParagraphs?: boolean;
  preserveHeaders?: boolean;
  preserveLists?: boolean;
  preserveTables?: boolean;
  maxLineLength?: number;
  addSpacing?: boolean;
}

/**
 * Formats extracted PDF text to improve readability and visual appeal
 */
export const formatPdfText = (
  text: string,
  options: TextFormattingOptions = {}
): string => {
  const {
    preserveParagraphs = true,
    preserveHeaders = true,
    preserveLists = true,
    preserveTables = true,
    maxLineLength = 80,
    addSpacing = true
  } = options;

  if (!text) return '';

  let formattedText = text;

  // Step 1: Clean up basic formatting issues
  formattedText = formattedText
    // Remove excessive whitespace while preserving intentional breaks
    .replace(/\s+/g, ' ')
    // Normalize line endings
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Remove excessive newlines
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // Step 2: Detect and preserve document structure
  if (preserveHeaders) {
    // Detect potential headers (lines that are shorter and might be titles)
    formattedText = formattedText.replace(
      /^(.{1,50})\n/gm,
      (match, line) => {
        const trimmed = line.trim();
        // If line is short and followed by content, it might be a header
        if (trimmed.length < 50 && trimmed.length > 0) {
          return `\n## ${trimmed}\n`;
        }
        return match;
      }
    );
  }

  // Step 3: Detect and format lists
  if (preserveLists) {
    // Detect numbered lists
    formattedText = formattedText.replace(
      /^(\d+\.\s+)(.+)$/gm,
      '\n$1$2\n'
    );
    
    // Detect bullet points
    formattedText = formattedText.replace(
      /^([•·▪▫‣⁃]\s+)(.+)$/gm,
      '\n• $2\n'
    );
    
    // Detect dash lists
    formattedText = formattedText.replace(
      /^(-\s+)(.+)$/gm,
      '\n• $2\n'
    );
  }

  // Step 4: Detect and preserve paragraphs
  if (preserveParagraphs) {
    // Group related sentences into paragraphs
    formattedText = formattedText.replace(
      /([.!?])\s+/g,
      '$1\n\n'
    );
    
    // Clean up excessive paragraph breaks
    formattedText = formattedText.replace(/\n{4,}/g, '\n\n');
  }

  // Step 5: Format tables (basic detection)
  if (preserveTables) {
    // Detect table-like structures with multiple columns
    formattedText = formattedText.replace(
      /^(.+)\s{2,}(.+)\s{2,}(.+)$/gm,
      (match, col1, col2, col3) => {
        // If we have multiple columns with significant spacing, format as table
        if (col1.length > 5 && col2.length > 5 && col3.length > 5) {
          return `\n| ${col1.trim()} | ${col2.trim()} | ${col3.trim()} |\n`;
        }
        return match;
      }
    );
  }

  // Step 6: Add proper spacing and formatting
  if (addSpacing) {
    // Add spacing around headers
    formattedText = formattedText.replace(/(## .+)/g, '\n$1\n');
    
    // Add spacing around lists
    formattedText = formattedText.replace(/(• .+)/g, '\n$1');
    
    // Add spacing around tables
    formattedText = formattedText.replace(/(\| .+ \|)/g, '\n$1\n');
  }

  // Step 7: Final cleanup
  formattedText = formattedText
    // Remove excessive whitespace
    .replace(/\n{3,}/g, '\n\n')
    // Ensure consistent spacing
    .replace(/\n\s+\n/g, '\n\n')
    // Trim whitespace from each line
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .join('\n')
    .trim();

  return formattedText;
};

/**
 * Formats text for display with proper line breaks and spacing
 */
export const formatTextForDisplay = (
  text: string,
  options: TextFormattingOptions = {}
): string => {
  const formatted = formatPdfText(text, options);
  
  // Convert markdown-like formatting to display-friendly text
  return formatted
    .replace(/## (.+)/g, '$1') // Remove header markers
    .replace(/\| (.+) \|/g, '$1') // Remove table markers
    .trim();
};

/**
 * Detects document sections and adds visual separators
 */
export const addSectionSeparators = (text: string): string => {
  return text
    .replace(/(\n)([A-Z][A-Z\s]{2,})(\n)/g, '$1\n━━━ $2 ━━━\n$3')
    .replace(/(\n)(\d+\.\s+[A-Z][^.\n]{2,})(\n)/g, '$1\n━━━ $2 ━━━\n$3');
};

/**
 * Formats text with enhanced readability features
 */
export const enhanceTextReadability = (
  text: string,
  options: TextFormattingOptions = {}
): string => {
  let enhanced = formatPdfText(text, options);
  
  // Add section separators for better visual organization
  enhanced = addSectionSeparators(enhanced);
  
  // Add reading progress indicators
  enhanced = enhanced.replace(
    /(Page \d+)/gi,
    '📄 $1'
  );
  
  // Add emphasis to important terms
  enhanced = enhanced.replace(
    /\b(Definition|Example|Note|Important|Warning|Tip)\b/gi,
    '💡 $1'
  );

  // Add emphasis to key concepts (words that appear multiple times)
  const words = enhanced.split(/\s+/);
  const wordCount: { [key: string]: number } = {};
  
  words.forEach(word => {
    const cleanWord = word.toLowerCase().replace(/[^\w]/g, '');
    if (cleanWord.length > 3) {
      wordCount[cleanWord] = (wordCount[cleanWord] || 0) + 1;
    }
  });

  // Highlight frequently occurring important terms
  Object.entries(wordCount).forEach(([word, count]) => {
    if (count >= 3 && word.length > 4) {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      enhanced = enhanced.replace(regex, `**${word}**`);
    }
  });

  // Clean up markdown formatting for display
  enhanced = enhanced.replace(/\*\*(.+?)\*\*/g, '$1');
  
  return enhanced;
};

/**
 * Smart text chunking for better page breaks
 */
export const smartTextChunking = (
  text: string,
  targetChunkSize: number = 2000
): string[] => {
  const sentences = text.split(/(?<=[.!?])\s+/);
  const chunks: string[] = [];
  let currentChunk = '';

  sentences.forEach(sentence => {
    if ((currentChunk + sentence).length > targetChunkSize && currentChunk.length > 0) {
      chunks.push(currentChunk.trim());
      currentChunk = sentence;
    } else {
      currentChunk += (currentChunk ? ' ' : '') + sentence;
    }
  });

  if (currentChunk.trim()) {
    chunks.push(currentChunk.trim());
  }

  return chunks;
};
